/**
 * Počet HP v popupu budovy — spec má prioritu; fallback na import (object_acquisition.hp_count).
 *
 * HP = home points (bytové jednotky) — `pripojovany_objekt_spec.hp_count_total`.
 */

/** VSO = velký stavební objekt (HP ≥ 4). MSO = HP ≤ 3. */
export const VSO_HP_MIN = 4;

/** Backend / UI: počet digitálních cest nesmí překročit počet HP. */
export const DIGITALNI_CESTA_EXCEEDS_HP_MESSAGE =
  'Počet digitálních cest nesmí být vyšší než počet HP.';

/** Krátký hint u stepperu (+/− digitální cesty). */
export const DIGITALNI_CESTA_HP_HINT = 'Max. počet = počet HP';

/** VSO = velký stavební objekt (HP ≥ 4). MSO = HP ≤ 3. */
export function isVsoHp(hp: number): boolean {
  return Number.isFinite(hp) && hp >= VSO_HP_MIN;
}

/** MSO = malý stavební objekt (0 ≤ HP ≤ 3). */
export function isMsoHp(hp: number): boolean {
  return Number.isFinite(hp) && hp >= 0 && hp < VSO_HP_MIN;
}

/**
 * Maximální povolený počet digitálních cest pro dané HP.
 * HP 0 / null / neplatné → 0 (digitální cesta musí být 0, dokud není HP nastaveno).
 */
export function maxDigitalniCestaCount(hp: number | null | undefined): number {
  if (hp == null) return 0;
  const n = Math.trunc(Number(hp));
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/**
 * Lze nastavit počet digitálních cest `n` při daném počtu HP?
 * `n === 0` je vždy OK; jinak `0 < n ≤ HP` (HP 0/null → jen 0).
 */
export function canSetDigitalniCestaCount(
  hp: number | null | undefined,
  n: number | null | undefined,
): boolean {
  const count = Math.trunc(Number(n ?? 0));
  if (!Number.isFinite(count) || count < 0) return false;
  return count <= maxDigitalniCestaCount(hp);
}

/** Ořízne počet digitálních cest na 0..maxDigitalniCestaCount(hp). */
export function clampDigitalniCestaCount(
  hp: number | null | undefined,
  n: number | null | undefined,
): number {
  const raw = Math.trunc(Number(n ?? 0));
  const count = Number.isFinite(raw) ? Math.max(0, raw) : 0;
  return Math.min(count, maxDigitalniCestaCount(hp));
}

export function resolveBudovaHpCountForPopup(input: {
  specHpTotal?: number | null;
  specSummaryHpTotal?: number | null;
  acquisitionHpCount?: number | null;
}): number | null {
  const specHp = input.specHpTotal;
  if (specHp != null && Number.isInteger(specHp) && specHp > 0) {
    return specHp;
  }
  const summaryHp = input.specSummaryHpTotal;
  if (summaryHp != null && Number.isInteger(summaryHp) && summaryHp > 0) {
    return summaryHp;
  }
  const acqHp = input.acquisitionHpCount;
  if (acqHp != null && Number.isInteger(acqHp) && acqHp > 0) {
    return acqHp;
  }
  if (specHp != null && Number.isInteger(specHp) && specHp >= 0) {
    return specHp;
  }
  if (summaryHp != null && Number.isInteger(summaryHp) && summaryHp >= 0) {
    return summaryHp;
  }
  if (acqHp != null && Number.isInteger(acqHp) && acqHp >= 0) {
    return acqHp;
  }
  return null;
}
